import React from 'react';
import { getIconEntry, FALLBACK_ICON } from '../../design-system/iconRegistry';
import { useDirection } from '../../i18n/direction';

/**
 * P8-P0 — Design System icon resolver.  (dev-only fallback counter added in PR3)
 *
 * Renders the registered icon for `name`, forwarding ALL props verbatim — so a
 * migrated `<Icon name="x" className="h-4 w-4" />` is pixel-identical to the
 * original `<X className="h-4 w-4" />`.
 *
 * Safety: an unknown `name` renders a neutral fallback glyph (never blank /
 * never throws) and, in development only, logs a warning + records the miss on
 * `window.__DS_ICON_MISSES__` so developers can detect fallback usage during
 * the migration. This instrumentation is fully stripped/no-op in production
 * builds (guarded by `process.env.NODE_ENV !== 'production'`) and never renders
 * any user-facing UI.
 *
 * P9-P0: RTL mirroring is now active — when the current direction is `rtl` AND
 * the registry marks this icon as directional (`rtlFlip`), a horizontal flip
 * (`transform: scaleX(-1)`) is applied. In the default LTR direction NOTHING
 * changes (behaviour-neutral / pixel-identical to before).
 */
const IS_DEV = process.env.NODE_ENV !== 'production';

function recordMiss(name) {
  if (!IS_DEV || typeof window === 'undefined') return;
  const store = (window.__DS_ICON_MISSES__ = window.__DS_ICON_MISSES__ || { count: 0, names: {} });
  store.count += 1;
  store.names[name] = (store.names[name] || 0) + 1;
  // eslint-disable-next-line no-console
  console.warn(`[Icon] Unknown icon name "${name}" — rendering fallback. (total misses: ${store.count})`);
}

export const Icon = React.forwardRef(({ name, ...props }, ref) => {
  const { isRtl } = useDirection();
  const entry = getIconEntry(name);

  if (!entry) {
    recordMiss(name);
    const Fallback = FALLBACK_ICON;
    return <Fallback ref={ref} {...props} />;
  }

  // RTL flip: only when direction is rtl AND this glyph is directional.
  const shouldFlip = isRtl && entry.rtlFlip;
  let finalProps = props;
  if (shouldFlip) {
    const baseTransform = props.style?.transform ? `${props.style.transform} ` : '';
    finalProps = { ...props, style: { ...props.style, transform: `${baseTransform}scaleX(-1)` } };
  }

  const Comp = entry.Comp;
  return <Comp ref={ref} {...finalProps} />;
});

Icon.displayName = 'Icon';

export default Icon;
